import { useState, useEffect, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useUpdateAuthUserRelations, OrganizationMember, AuthUserRelationChange } from "@/lib/api/betterAuth";
import { useToast } from "@/components/ui/use-toast";
import { useListCustomers } from "@/lib/api/customer";
import { useVirtualizer } from "@tanstack/react-virtual";
import { cn } from "@/lib/utils";

interface CustomerAssignmentEditorProps {
  member: OrganizationMember;
  relationType: string;
  onSuccess?: () => void;
  inline?: boolean;
  maxAllowed?: number;
}

interface Customer {
  id: string;
  long_name?: string;
  short_name?: string;
  hasRiskProfile?: boolean;
}

export function CustomerAssignmentEditor({
  member,
  relationType,
  onSuccess,
  inline = false,
  maxAllowed,
}: CustomerAssignmentEditorProps) {
  const { toast } = useToast();
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const selectedListRef = useRef<HTMLDivElement>(null);
  const availableListRef = useRef<HTMLDivElement>(null);

  const updateRelationsMutation = useUpdateAuthUserRelations();
  const customersRes = useListCustomers({ minimal: true });
  const customers = (customersRes?.data?.data || []) as Customer[];

  // Filter customers based on search query (only for available list)
  const filteredCustomers = useMemo(() => {
    let result = customers;
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (customer) =>
          customer.long_name?.toLowerCase().includes(query)
      );
    }
    
    return result;
  }, [customers, searchQuery]);

  // Selected customers: show ALL selected customers regardless of search query
  const selectedCustomers = useMemo(() => {
    return customers.filter((customer) => selectedCustomerIds.has(customer.id));
  }, [customers, selectedCustomerIds]);

  // Available customers: exclude selected ones, apply search filter, and if relationType is "risk", exclude customers without risk profile
  const availableCustomers = useMemo(() => {
    let result = filteredCustomers.filter((customer) => !selectedCustomerIds.has(customer.id));
    
    // If relation type is "risk", filter out customers without risk profile
    if (relationType === "risk") {
      result = result.filter((customer) => customer.hasRiskProfile);
    }
    
    return result;
  }, [filteredCustomers, selectedCustomerIds, relationType]);

  // Initialize selected customers from member relations
  useEffect(() => {
    if (member && customers.length > 0) {
      const existingRelationIds = new Set(
        (member.relations || [])
          .filter((rel) => rel.relation_type === relationType)
          .map((rel) => rel.customer_id)
      );
      setSelectedCustomerIds(existingRelationIds);
      setHasChanges(false);
    }
  }, [member, customers, relationType]);

  const selectedVirtualizer = useVirtualizer({
    count: selectedCustomers.length,
    getScrollElement: () => selectedListRef.current,
    estimateSize: () => 36, // Dense card height
    overscan: 10,
  });

  const availableVirtualizer = useVirtualizer({
    count: availableCustomers.length,
    getScrollElement: () => availableListRef.current,
    estimateSize: () => 36, // Dense card height
    overscan: 30,
  });

  const handleCustomerToggle = (customerId: string) => {
    setSelectedCustomerIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(customerId)) {
        newSet.delete(customerId);
      } else {
        // If maxAllowed is set to 1, clear existing selections when adding a new one
        if (maxAllowed === 1) {
          newSet.clear();
        }
        newSet.add(customerId);
      }
      setHasChanges(true);
      return newSet;
    });
  };

  const calculateChanges = (): AuthUserRelationChange[] => {
    if (!member?.authuser?.id) return [];

    // Get existing relations for this relation type
    const existingRelations = (member.relations || []).filter(
      (rel) => rel.relation_type === relationType
    );
    const existingCustomerIds = new Set(
      existingRelations.map((rel) => rel.customer_id)
    );

    // Calculate changes: additions and deletions
    const changes: AuthUserRelationChange[] = [];

    // Additions: selected but not in existing relations
    selectedCustomerIds.forEach((customerId) => {
      if (!existingCustomerIds.has(customerId)) {
        changes.push({
          user_id: member.authuser!.id,
          customer_id: customerId,
          relation_type: relationType as "risk",
        });
      }
    });

    // Deletions: in existing relations but not selected
    existingRelations.forEach((rel) => {
      if (!selectedCustomerIds.has(rel.customer_id)) {
        changes.push({
          user_id: member.authuser!.id,
          customer_id: rel.customer_id,
          relation_type: relationType as "risk",
          should_delete: true,
        });
      }
    });

    return changes;
  };

  const handleSave = async () => {
    if (!member?.authuser?.id) {
      toast({
        title: "Error",
        description: "User ID is required",
        variant: "destructive",
      });
      return;
    }

    const changes = calculateChanges();

    if (changes.length === 0) {
      toast({
        title: "No changes",
        description: "No changes to save",
      });
      return;
    }

    try {
      const result = await updateRelationsMutation.mutateAsync({ changes });
      if (!result.success) {
        throw result;
      }

      const additionsCount = changes.filter((c) => !c.should_delete).length;
      const deletionsCount = changes.filter((c) => c.should_delete).length;

      let description = "";
      if (additionsCount > 0 && deletionsCount > 0) {
        description = `Successfully updated: ${additionsCount} added, ${deletionsCount} removed`;
      } else if (additionsCount > 0) {
        description = `Successfully assigned ${additionsCount} customer${additionsCount !== 1 ? "s" : ""}`;
      } else if (deletionsCount > 0) {
        description = `Successfully removed ${deletionsCount} customer${deletionsCount !== 1 ? "s" : ""}`;
      }

      toast({
        title: "Success",
        description,
      });

      setHasChanges(false);
      onSuccess?.();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to update customer assignments",
        variant: "destructive",
      });
    }
  };

  const handleReset = () => {
    const existingRelationIds = new Set(
      (member.relations || [])
        .filter((rel) => rel.relation_type === relationType)
        .map((rel) => rel.customer_id)
    );
    setSelectedCustomerIds(existingRelationIds);
    setHasChanges(false);
  };

  const displayName = (customer: Customer) => {
    return customer.long_name || customer.short_name || "Unknown";
  };

  const renderCustomerCard = (
    customer: Customer,
    virtualItem: any,
    virtualizer: any,
    isSelected: boolean
  ) => {
    return (
      <div
        key={customer.id}
        data-index={virtualItem.index}
        ref={virtualizer.measureElement}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          transform: `translateY(${virtualItem.start}px)`,
        }}
      >
        <div
          onClick={() => handleCustomerToggle(customer.id)}
          className={cn(
            "px-3 py-1.5 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0",
            isSelected
              ? "bg-blue-50 border-blue-200 hover:bg-blue-100"
              : "hover:bg-gray-50"
          )}
          style={{ minHeight: "32px" }}
        >
          <span
            className={cn(
              "text-sm",
              isSelected ? "font-medium text-blue-900" : "text-gray-900"
            )}
          >
            {displayName(customer)}
          </span>
        </div>
      </div>
    );
  };

  const selectedVirtualItems = selectedVirtualizer.getVirtualItems();
  const availableVirtualItems = availableVirtualizer.getVirtualItems();

  // Check if max allowed is exceeded
  const isMaxExceeded = maxAllowed !== undefined && selectedCustomerIds.size > maxAllowed;
  const isSaveDisabled = updateRelationsMutation.isLoading || isMaxExceeded;

  return (
    <div className="flex-1 overflow-hidden flex flex-col space-y-4 min-h-0">
      <div className="space-y-2 flex-shrink-0">
        <Label htmlFor="search">Search Customers</Label>
        <Input
          id="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by customer name..."
          disabled={updateRelationsMutation.isLoading}
        />
      </div>

      {/* Selected Customers List */}
      {selectedCustomers.length > 0 && (
        <div className="flex flex-col space-y-2 flex-shrink-0">
          <Label className="text-sm font-medium text-gray-700">
            Selected ({selectedCustomers.length}{maxAllowed !== undefined ? ` / ${maxAllowed}` : ""})
            {isMaxExceeded && (
              <span className="ml-2 text-xs text-red-600 font-normal">
                Maximum exceeded
              </span>
            )}
          </Label>
          <div
            ref={selectedListRef}
            className="overflow-y-auto border rounded-lg bg-blue-50/30"
            style={{ maxHeight: "200px", minHeight: "100px" }}
          >
            <div
              style={{
                height: `${selectedVirtualizer.getTotalSize()}px`,
                width: "100%",
                position: "relative",
              }}
            >
              {selectedVirtualItems.map((virtualItem) => {
                const customer = selectedCustomers[virtualItem.index];
                return renderCustomerCard(
                  customer,
                  virtualItem,
                  selectedVirtualizer,
                  true
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Available Customers List */}
      <div className="flex flex-col space-y-2 flex-1 min-h-0">
        <Label className="text-sm font-medium text-gray-700">
          Available ({availableCustomers.length})
        </Label>
        <div
          ref={availableListRef}
          className="flex-1 overflow-y-auto border rounded-lg"
          style={{ maxHeight: "400px", minHeight: "200px" }}
        >
          {availableCustomers.length === 0 ? (
            <div className="flex items-center justify-center h-full py-8">
              <p className="text-sm text-gray-500">
                {searchQuery ? "No customers found" : "No customers available"}
              </p>
            </div>
          ) : (
            <div
              style={{
                height: `${availableVirtualizer.getTotalSize()}px`,
                width: "100%",
                position: "relative",
              }}
            >
              {availableVirtualItems.map((virtualItem) => {
                const customer = availableCustomers[virtualItem.index];
                return renderCustomerCard(
                  customer,
                  virtualItem,
                  availableVirtualizer,
                  false
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Action buttons for non-inline mode */}
      {!inline && hasChanges && (
        <div className="flex justify-between pt-4 border-t flex-shrink-0">
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={updateRelationsMutation.isLoading}
          >
            Reset Changes
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaveDisabled}
            title={isMaxExceeded ? `Maximum of ${maxAllowed} customer${maxAllowed !== 1 ? 's' : ''} allowed` : undefined}
          >
            {updateRelationsMutation.isLoading ? "Saving..." : "Save Assignments"}
          </Button>
        </div>
      )}

      {/* For inline mode, save immediately on change */}
      {inline && hasChanges && (
        <div className="flex justify-end pt-4 border-t flex-shrink-0">
          <Button
            onClick={handleSave}
            disabled={isSaveDisabled}
            title={isMaxExceeded ? `Maximum of ${maxAllowed} customer${maxAllowed !== 1 ? 's' : ''} allowed` : undefined}
          >
            {updateRelationsMutation.isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      )}
    </div>
  );
}
